import { useState, useRef, useEffect, type ReactNode } from 'react';
import { trpc } from '../api/trpc';
import { CadeAvatar } from './CadeAvatar';
import { ChatSidebarItem } from './ChatSidebarItem';
import { DeleteChatDialog } from './DeleteChatDialog';

interface Props {
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onSearch: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  onNavigateToAgents?: () => void;
  onNavigateToAgent?: (agentId: string) => void;
  onNavigateToProjects?: () => void;
  onNavigateToProject?: (projectId: string) => void;
  onNavigateToCryptoInventory?: () => void;
  onNavigateHome?: () => void;
  /** Mobile: render as an off-canvas drawer rather than the desktop icon-rail/expanded sidebar. */
  isMobile?: boolean;
  /** Mobile: whether the drawer is slid in. */
  mobileOpen?: boolean;
  /** Mobile: close the drawer (backdrop tap, in-drawer chevron, or after navigating). */
  onMobileClose?: () => void;
}

interface ConvListItem {
  id: string;
  title?: string | null;
  updatedAt: string | Date;
  unreadCount?: number;
  pinnedAt?: string | Date | null;
  participantCount?: number;
  myRole?: string;
  project?: { id: string; title: string } | null;
  platformRef?: { platform?: string } | null;
}

export function formatRelativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d`;
  return new Date(date).toLocaleDateString(undefined, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

function sortConversations(convs: ConvListItem[]): ConvListItem[] {
  return [...convs].sort((a, b) =>
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

/* ── Sub-components ── */

function SidebarSection({ label, icon, defaultOpen = true, trailing, children }: { label: string; icon?: ReactNode; defaultOpen?: boolean; trailing?: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`sb-section ${open ? '' : 'collapsed'}`}>
      <div className="sb-section-header">
        <div className="sb-section-header-left" onClick={() => setOpen(o => !o)}>
          {icon && <span className="sb-section-icon">{icon}</span>}
          <span className="sb-section-label">{label}</span>
        </div>
        {trailing && <div className="sb-section-header-trailing">{trailing}</div>}
        <svg className="sb-section-chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}><polyline points="6 9 12 15 18 9" /></svg>
      </div>
      <div className="sb-section-items" style={{ maxHeight: open ? '2000px' : '0' }}>
        {children}
      </div>
    </div>
  );
}

function SidebarItem({ icon, text, meta, active, unreadCount, onClick }: { icon?: 'folder' | 'bot' | 'chat' | 'pen' | 'shield'; text: string; meta?: string; active?: boolean; unreadCount?: number; onClick?: () => void }) {
  const icons = {
    folder: <svg className="sb-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>,
    shield: <svg className="sb-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
    bot: <svg className="sb-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" /></svg>,
    chat: <svg className="sb-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>,
    pen: <svg className="sb-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>,
  };
  const hasUnread = (unreadCount ?? 0) > 0;
  return (
    <button className={`sb-item ${active ? 'active' : ''} ${hasUnread ? 'unread' : ''}`} onClick={onClick}>
      {icon && icons[icon]}
      <span className="sb-item-text">{text}</span>
      {hasUnread
        ? <span className="sb-unread-badge">{unreadCount! > 99 ? '99+' : unreadCount}</span>
        : meta && <span className="sb-item-meta">{meta}</span>
      }
    </button>
  );
}

/* ── Pin section icon ── */
const PinSectionIcon = (
  <svg className="sb-section-icon-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="17" x2="12" y2="22"/>
    <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/>
  </svg>
);

/* ── Main component ── */

export function ConversationSidebar({ activeId, onSelect, onNew, onSearch, collapsed, onToggleCollapse, onNavigateToAgents, onNavigateToAgent: _onNavigateToAgent, onNavigateToProjects, onNavigateToProject: _onNavigateToProject, onNavigateToCryptoInventory, onNavigateHome, isMobile = false, mobileOpen = false, onMobileClose }: Props) {
  const { data } = trpc.conversations.list.useQuery({});
  const utils = trpc.useUtils();
  const knownIdsRef = useRef<Set<string>>(new Set());
  const [newIds, setNewIds] = useState<Set<string>>(new Set());
  const [contextMenuId, setContextMenuId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; isGroup: boolean } | null>(null);
  const [projectPickerConvId, setProjectPickerConvId] = useState<string | null>(null);
  const [projectPickerCurrentProject, setProjectPickerCurrentProject] = useState<{ id: string; title: string } | null>(null);
  const [pinnedUnreadOnly, setPinnedUnreadOnly] = useState(false);
  const [chatsUnreadOnly, setChatsUnreadOnly] = useState(false);

  // Mutations with optimistic updates
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  type ListData = any;

  const pinMutation = trpc.conversations.pin.useMutation({
    onMutate: async ({ conversationId }) => {
      await utils.conversations.list.cancel();
      const prev = utils.conversations.list.getData({});
      utils.conversations.list.setData({}, (old: ListData) =>
        old?.map((c: ListData) => c.id === conversationId ? { ...c, pinnedAt: new Date().toISOString() } : c)
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.conversations.list.setData({}, context.prev);
    },
    onSettled: () => utils.conversations.list.invalidate(),
  });

  const unpinMutation = trpc.conversations.unpin.useMutation({
    onMutate: async ({ conversationId }) => {
      await utils.conversations.list.cancel();
      const prev = utils.conversations.list.getData({});
      utils.conversations.list.setData({}, (old: ListData) =>
        old?.map((c: ListData) => c.id === conversationId ? { ...c, pinnedAt: null } : c)
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.conversations.list.setData({}, context.prev);
    },
    onSettled: () => utils.conversations.list.invalidate(),
  });

  const deleteMutation = trpc.conversations.delete.useMutation({
    onMutate: async ({ conversationId }) => {
      await utils.conversations.list.cancel();
      const prev = utils.conversations.list.getData({});
      utils.conversations.list.setData({}, (old: ListData) =>
        old?.filter((c: ListData) => c.id !== conversationId)
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) utils.conversations.list.setData({}, context.prev);
    },
    onSettled: () => { void utils.conversations.list.invalidate(); void utils.home.getFeed.invalidate(); },
  });

  const linkToProject = trpc.projects.linkConversation.useMutation({
    onSettled: () => { void utils.conversations.list.invalidate(); void utils.projects.listConversations.invalidate(); void utils.projects.get.invalidate(); },
  });

  const unlinkFromProject = trpc.projects.unlinkConversation.useMutation({
    onSettled: () => { void utils.conversations.list.invalidate(); void utils.projects.listConversations.invalidate(); void utils.projects.get.invalidate(); },
  });

  const updateTitle = trpc.conversations.updateTitle.useMutation({
    onSettled: () => {
      void utils.conversations.list.invalidate();
    },
  });

  // Track newly appearing conversations for animation
  useEffect(() => {
    if (!data) return;
    const currentIds = new Set<string>(data.map((c: { id: string }) => c.id));
    if (knownIdsRef.current.size > 0) {
      const fresh = new Set<string>();
      for (const id of currentIds) {
        if (!knownIdsRef.current.has(id)) fresh.add(id);
      }
      if (fresh.size > 0) {
        setNewIds(fresh);
        const timer = setTimeout(() => setNewIds(new Set()), 600);
        return () => clearTimeout(timer);
      }
    }
    knownIdsRef.current = currentIds;
  }, [data]);

  // Split conversations into pinned and unpinned, apply unread filter
  const conversations = (data ?? []) as ConvListItem[];
  const allPinned = sortConversations(conversations.filter(c => c.pinnedAt != null));
  const allUnpinned = sortConversations(conversations.filter(c => c.pinnedAt == null));
  const pinned = pinnedUnreadOnly ? allPinned.filter(c => (c.unreadCount ?? 0) > 0) : allPinned;
  const unpinned = chatsUnreadOnly ? allUnpinned.filter(c => (c.unreadCount ?? 0) > 0) : allUnpinned;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteMutation.mutate({ conversationId: deleteTarget.id });
    // If deleting the active conversation, deselect
    if (deleteTarget.id === activeId) {
      onSelect('');
    }
    setDeleteTarget(null);
  }

  function renderChatItem(conv: ConvListItem) {
    const isGroup = (conv.participantCount ?? 1) > 1;
    const canRename = !isGroup || conv.myRole === 'owner';

    return (
      <div key={conv.id} className={newIds.has(conv.id) ? 'sb-item-appear' : ''}>
        <ChatSidebarItem
          conversationId={conv.id}
          title={conv.title ?? 'New chat'}
          meta={formatRelativeTime(conv.updatedAt)}
          active={conv.id === activeId}
          unreadCount={conv.unreadCount ?? 0}
          isPinned={conv.pinnedAt != null}
          isGroupChat={isGroup}
          canRename={canRename}
          isMenuOpen={contextMenuId === conv.id}
          isRenaming={renamingId === conv.id}
          onSelect={() => onSelect(conv.id)}
          onPin={() => pinMutation.mutate({ conversationId: conv.id })}
          onUnpin={() => unpinMutation.mutate({ conversationId: conv.id })}
          onRenameStart={() => setRenamingId(conv.id)}
          onRenameConfirm={(title) => {
            updateTitle.mutate({ conversationId: conv.id, title });
            setRenamingId(null);
          }}
          onRenameCancel={() => setRenamingId(null)}
          onDelete={() => setDeleteTarget({ id: conv.id, isGroup })}
          projectId={conv.project?.id ?? null}
          projectName={conv.project?.title ?? null}
          surfacePlatform={(() => {
            const pr = conv.platformRef as { platform?: string; surface?: string } | null;
            // `surface` is the canonical field on the stored PlatformRef (slack,
            // servicenow, …); `platform` kept as a fallback for older rows.
            return pr?.surface ?? pr?.platform ?? null;
          })()}
          onAddToProject={() => { setContextMenuId(null); setProjectPickerCurrentProject(null); setProjectPickerConvId(conv.id); }}
          onChangeProject={() => { setContextMenuId(null); setProjectPickerCurrentProject(conv.project ? { id: conv.project.id, title: conv.project.title } : null); setProjectPickerConvId(conv.id); }}
          onRemoveFromProject={() => { unlinkFromProject.mutate({ conversationId: conv.id }); setContextMenuId(null); }}
          onMenuOpen={() => setContextMenuId(conv.id)}
          onMenuClose={() => setContextMenuId(null)}
        />
      </div>
    );
  }

  // On mobile the sidebar is an off-canvas drawer: always render expanded content,
  // add the drawer positioning class, and slide in when open.
  const asideClass = isMobile
    ? `sidebar expanded sidebar-mobile ${mobileOpen ? 'mobile-open' : ''}`
    : `sidebar ${collapsed ? '' : 'expanded'}`;

  return (
    <aside className={asideClass}>
      {/* Fixed top area */}
      <div className="sb-top">
        {/* Header */}
        <div className="sb-head">
          <div className="sb-logo-row" onClick={isMobile ? onNavigateHome : (collapsed ? onToggleCollapse : onNavigateHome)} style={{ cursor: 'pointer' }}>
            <div className="sb-logo-btn">
              <CadeAvatar size={36} disc />
              <svg className="sb-logo-expand" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="sb-wordmark">Cade</span>
          </div>
          <button
            className="ib xs sb-collapse-btn"
            onClick={isMobile ? onMobileClose : onToggleCollapse}
            title={isMobile ? 'Close menu' : 'Collapse sidebar'}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* New chat (expanded only) */}
        <div className="sb-actions">
          <SidebarItem icon="pen" text="New chat" onClick={onNew} />
        </div>

        {/* Search (expanded only) */}
        <div className="sb-search">
          <button className="sb-search-btn" type="button" onClick={onSearch}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <span>Search chats</span>
          </button>
        </div>

        {/* Nav links (expanded only, sticky with sb-top) */}
        <div className="sb-nav-links">
          <SidebarItem icon="folder" text="Projects" onClick={onNavigateToProjects} />
          <SidebarItem icon="bot" text="Task Agents" onClick={onNavigateToAgents} />
          <SidebarItem icon="shield" text="Crypto Inventory" onClick={onNavigateToCryptoInventory} />
        </div>
      </div>

      {/* Collapsed nav icons */}
      <div className="sb-nav">
        <button className="ib" onClick={onNew} title="New chat">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
          </svg>
        </button>
        <button className="ib" onClick={onSearch} title="Search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
        </button>
        <div className="sb-divider" />
        <button className="ib" title="Projects" onClick={onNavigateToProjects}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" /></svg>
        </button>
        <button className="ib" title="Task Agents" onClick={onNavigateToAgents}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><line x1="8" y1="16" x2="8" y2="16" /><line x1="16" y1="16" x2="16" y2="16" /></svg>
        </button>
        <button className="ib" title="Crypto Inventory" onClick={onNavigateToCryptoInventory}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
        </button>
        <button className="ib" title="Chats">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
        </button>
      </div>

      {/* Scrollable list */}
      <div className="sb-list">
        {allPinned.length > 0 && (
          <SidebarSection
            label="Pinned"
            icon={PinSectionIcon}
            trailing={
              <button
                className={`ib xs sb-filter-toggle ${pinnedUnreadOnly ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setPinnedUnreadOnly(v => !v); }}
                title={pinnedUnreadOnly ? 'Show all pinned' : 'Show unread only'}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" fill={pinnedUnreadOnly ? 'currentColor' : 'none'} />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" />
                </svg>
              </button>
            }
          >
            {pinned.length > 0
              ? pinned.map(renderChatItem)
              : pinnedUnreadOnly && <div className="sb-empty-filter">No unread pinned chats</div>
            }
          </SidebarSection>
        )}

        <SidebarSection
          label="Chats"
          icon={<svg className="sb-section-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>}
          trailing={
            <button
              className={`ib xs sb-filter-toggle ${chatsUnreadOnly ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setChatsUnreadOnly(v => !v); }}
              title={chatsUnreadOnly ? 'Show all chats' : 'Show unread only'}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="18" cy="5" r="3" fill={chatsUnreadOnly ? 'currentColor' : 'none'} />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" />
              </svg>
            </button>
          }
        >
          {unpinned.length > 0
            ? unpinned.map(renderChatItem)
            : chatsUnreadOnly && <div className="sb-empty-filter">No unread chats</div>
          }
        </SidebarSection>
      </div>

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <DeleteChatDialog
          isGroupChat={deleteTarget.isGroup}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Project picker overlay */}
      {projectPickerConvId && (
        <ProjectPickerPopup
          currentProjectId={projectPickerCurrentProject?.id}
          currentProjectName={projectPickerCurrentProject?.title}
          onSelect={(projectId) => {
            linkToProject.mutate({ projectId, conversationId: projectPickerConvId });
            setProjectPickerConvId(null);
          }}
          onClose={() => setProjectPickerConvId(null)}
        />
      )}
    </aside>
  );
}

/* ── Project picker popup ── */
function ProjectPickerPopup({ currentProjectId, currentProjectName, onSelect, onClose }: {
  currentProjectId?: string;
  currentProjectName?: string;
  onSelect: (projectId: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: projects } = trpc.projects.list.useQuery({});
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filtered = (projects ?? []).filter((p: any) => {
    if (p.id === currentProjectId) return false;
    if (!query.trim()) return true;
    return (p.title ?? '').toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="delete-dialog-overlay" onClick={onClose}>
      <div className="proj-dialog" onClick={e => e.stopPropagation()} style={{ width: 360 }}>
        <div className="proj-dialog-title">{currentProjectId ? 'Change project' : 'Add to project'}</div>
        {currentProjectId && currentProjectName && (
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            Currently in: <strong>{currentProjectName}</strong>
          </div>
        )}
        <input
          ref={inputRef}
          type="text"
          placeholder="Search projects..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{
            width: '100%', padding: '8px 12px', marginBottom: 12,
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius)',
            background: 'var(--color-surface)', color: 'var(--color-text-primary)',
            fontFamily: 'var(--font-sans)', fontSize: 13, outline: 'none',
          }}
        />
        <div style={{ maxHeight: 240, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding: '12px 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
              {projects?.length === 0 ? 'No projects yet' : 'No matching projects'}
            </div>
          )}
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {filtered.map((p: any) => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '8px 10px', border: 'none', background: 'none', cursor: 'pointer',
                borderRadius: 'var(--radius)', fontFamily: 'var(--font-sans)', fontSize: 13,
                color: 'var(--color-text-primary)', textAlign: 'left',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, color: 'var(--color-text-muted)' }}>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <span>{p.title}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
